import { useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { userApi } from '../../api/user.api';

interface BirthdateRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BirthdateRegisterModal = ({ isOpen, onClose, onSuccess }: BirthdateRegisterModalProps) => {
  const [birthdate, setBirthdate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validate = (value: string): string | null => {
    if (value.length !== 8) return '8자리 숫자로 입력해주세요';

    const year = parseInt(value.substring(0, 4), 10);
    const month = parseInt(value.substring(4, 6), 10);
    const day = parseInt(value.substring(6, 8), 10);

    if (year < 1900) return '1900년 이후로 입력해주세요';
    if (month < 1 || month > 12) return '올바른 월을 입력해주세요';
    if (day < 1 || day > 31) return '올바른 일을 입력해주세요';

    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return '존재하지 않는 날짜에요';
    }

    if (date > new Date()) return '미래 날짜는 입력할 수 없어요';

    return null;
  };

  const formatDisplay = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.substring(0, 4)}.${digits.substring(4)}`;
    return `${digits.substring(0, 4)}.${digits.substring(4, 6)}.${digits.substring(6, 8)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').substring(0, 8);
    setBirthdate(digits);
    setError('');
  };

  const handleRegister = async () => {
    const validationError = validate(birthdate);
    if (validationError) {
      setError(validationError);
      return;
    }

    const formatted = `${birthdate.substring(0, 4)}-${birthdate.substring(4, 6)}-${birthdate.substring(6, 8)}`;

    setIsLoading(true);
    setError('');
    try {
      const res = await userApi.registerBirthdate(formatted);
      const bonus = res.data?.bonusAmount;
      if (bonus) alert(`생년월일이 등록되었어요. 조각 ${bonus}개를 받았어요!`);
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('이미 등록된 생년월일은 변경할 수 없어요');
      } else {
        setError('생년월일 등록에 실패했습니다');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setBirthdate('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalHeader>
        <ModalTitle>생년월일을 입력해주세요</ModalTitle>
        <p className="text-sm text-amber-600 mt-1">입력하면 조각 1개 보너스! 한번 등록하면 수정할 수 없어요</p>
      </ModalHeader>
      <ModalContent>
        <div className="mb-6">
          <Input
            value={formatDisplay(birthdate)}
            onChange={handleChange}
            placeholder="19990101"
          />
        </div>
        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={handleClose} disabled={isLoading}>
            닫기
          </Button>
          <Button className="flex-1" onClick={handleRegister} disabled={birthdate.length !== 8 || isLoading}>
            {isLoading ? '등록 중...' : '등록'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default BirthdateRegisterModal;
